import { useTheme } from "@emotion/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CompanyService from "../../services/companyService";
import { Avatar, Box, Button, CircularProgress, Divider, Typography, useMediaQuery } from "@mui/material";
import Popup from "../../components/popups/popup";
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import CustomInput from "../../components/inputs/customInput";
import CustomInputWithSize from "../../components/inputs/customInputWithSize";
import UpdateImageUpload from "../../components/inputs/uploadImageUpdate";
import { fileApiRoute } from "../../services/networkService";
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import AddIcon from '@mui/icons-material/Add';

const CompanyInfo = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const inputRef = useRef();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));


    const [business, setBusiness] = useState({});

    const [formValues, setFormValues] = useState({
        nameKu: business.nameKu,
        nameEn: business.nameEn,
        nameAr: business.nameAr,
        aboutKu: business.aboutKu,
        aboutEn: business.aboutEn,
        aboutAr: business.aboutAr,
        addressOneKu: business.addressOneKu,
        addressOneEn: business.addressOneEn,
        addressOneAr: business.addressOneAr,
        addressTwoKu: business.addressTwoKu,
        addressTwoEn: business.addressTwoEn,
        addressTwoAr: business.addressTwoAr,
        phoneOne: business.phoneOne,
        phoneTwo: business.phoneTwo,
        deleteLogo: false,
    });

    const [updateErrors, setUpdateErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isModalLoading, setIsModalLoading] = useState(true);
    const [isImageLoading, setIsImageLoading] = useState(false);
    const [selectedLogo, setSelectedLogo] = useState(null);

    const [showPopup, setShowPopup] = useState(false);
    const [popupMsg, setPopupMsg] = useState(false);
    const [popupType, setPopupType] = useState(false);

    const handleButtonClick = () => {
        inputRef.current.click();
    };

    const handleLogoChange = (file) => {
        setSelectedLogo(file);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: value,
        }));
    };

    const validateForm = () => {
        const newUpdateErrors = {};
        setUpdateErrors(newUpdateErrors);
        return Object.keys(newUpdateErrors).length === 0;
    };


    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isLoading) return;

        if (validateForm()) {
            try {
                setIsLoading(true);
                const formData = new FormData();

                for (const key in formValues) {
                    if (formValues[key] && formValues[key] !== '') {
                        formData.append(key, formValues[key]);
                    }
                }

                if (selectedLogo) {
                    formData.append('logo', selectedLogo);
                }

                const data = await CompanyService.update(formData);
                if (data.status === 200) {
                    setPopupMsg(data.data.message);
                    setPopupType('success');
                    setShowPopup(true);
                }

                setUpdateErrors({});
            } catch (error) {
                if (error.response?.status === 400) {
                    const responseErrors = error.response.data || {};
                    const formattedErrors = {};

                    Object.keys(responseErrors).forEach((field) => {
                        formattedErrors[field] = responseErrors[field];
                    });

                    setUpdateErrors(formattedErrors);
                } else {
                    setPopupMsg(error.response.data.error);
                    setPopupType('error');
                    console.error("An unexpected error occurred:", error);
                    setUpdateErrors({ general: error.response.data.error });
                }
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        try {
            setIsImageLoading(true);
            const formData = new FormData();
            formData.append('image', file);

            const data = await CompanyService.addImage(formData);
            if (data.status === 200) {
                setIsImageLoading(false);
                setPopupMsg(data.data.message);
                setPopupType('success');
                setShowPopup(true);
                fetchBusiness();
            }
        } catch (error) {
            setPopupMsg(error.response.data.error);
            setPopupType('error');
            console.error("An unexpected error occurred:", error);
        }
    };

    const handleDelete = async (id) => {
        try {
            setIsImageLoading(true);
            const data = await CompanyService.deleteImage(id);
            if (data.status === 200) {
                setIsImageLoading(false);
                setPopupMsg(data.data.message);
                setPopupType('delete');
                setShowPopup(true);
                fetchBusiness();
            }

        } catch (error) {
            setPopupMsg(error.response.data.error);
            setPopupType('error');
            console.error("An unexpected error occurred:", error);
        }
    };

    const fetchBusiness = async () => {
        try {
            const data = await CompanyService.fetchOne();

            setBusiness(data);
            setIsModalLoading(false);
        } catch (error) {
            console.error('Error fetching business:', error);
        }
    };

    useEffect(() => {
        if (business) {
            setFormValues({
                nameKu: business.nameKu || '',
                nameEn: business.nameEn || '',
                nameAr: business.nameAr || '',
                aboutKu: business.aboutKu || '',
                aboutEn: business.aboutEn || '',
                aboutAr: business.aboutAr || '',
                addressOneKu: business.addressOneKu || '',
                addressOneEn: business.addressOneEn || '',
                addressOneAr: business.addressOneAr || '',
                addressTwoKu: business.addressTwoKu || '',
                addressTwoEn: business.addressTwoEn || '',
                addressTwoAr: business.addressTwoAr || '',
                phoneOne: business.phoneOne || '',
                phoneTwo: business.phoneTwo || '',
            });
            setUpdateErrors({});
        }
    }, [business]);


    useEffect(() => {
        setIsModalLoading(true);
        fetchBusiness();
    }, []);


    return (
        <Box sx={{
            p: isMobile ? 0.6 : 2,
            backgroundColor: theme.palette.secondary.main,
        }}>
            {showPopup && (
                <Popup
                    text={popupMsg}
                    type={popupType}
                    onClose={() => setShowPopup(false)}
                />
            )}

            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    width: '100%',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: 1,
                    borderColor: theme.palette.customGrey.borderInput,
                    p: isMobile ? 1 : 4
                }}
            >
                {
                    isModalLoading ? <Box sx={{ justifyContent: 'center', display: 'flex', my: 10 }} >
                        <CircularProgress color="primary" size={28} />
                    </Box>
                        : <Box>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between'
                            }}>
                                <Typography id="transition-modal-title" variant="h6" component="h2">
                                    {t('companyInformation')}
                                </Typography>
                            </Box>


                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    flexDirection: isMobile ? 'column' : 'row'
                                }}>
                                <Box sx={{
                                    width: isMobile ? '100%' : '60%'
                                }}>
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        flexDirection: isMobile ? 'column' : 'row'
                                    }}>
                                        <Box width={isMobile ? '100%' : '49%'}>
                                            <CustomInput
                                                label={t('nameKurdish')}
                                                placeholder={t('nameKurdishHere')}
                                                name="nameKu"
                                                type='text'
                                                value={formValues.nameKu}
                                                error={!!updateErrors.nameKu}
                                                helperText={updateErrors.nameKu}
                                                onChange={handleChange}
                                            />
                                        </Box>
                                        <Box width={isMobile ? '100%' : '49%'}>
                                            <CustomInput
                                                label={t('nameEnglish')}
                                                placeholder={t('nameEnglishHere')}
                                                name="nameEn"
                                                type='text'
                                                value={formValues.nameEn}
                                                error={!!updateErrors.nameEn}
                                                helperText={updateErrors.nameEn}
                                                onChange={handleChange}
                                            /></Box>
                                    </Box>

                                    <Box width={isMobile ? '100%' : '49%'}>
                                        <CustomInput
                                            label={t('nameArabic')}
                                            placeholder={t('nameArabicHere')}
                                            name="nameAr"
                                            type='text'
                                            value={formValues.nameAr}
                                            error={!!updateErrors.nameAr}
                                            helperText={updateErrors.nameAr}
                                            onChange={handleChange}
                                        /></Box>

                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        flexDirection: isMobile ? 'column' : 'row'
                                    }}>
                                        <Box width={isMobile ? '100%' : '49%'}>
                                            <CustomInput
                                                label={t('phone')}
                                                placeholder={t('phoneHere')}
                                                name="phoneOne"
                                                type='text'
                                                value={formValues.phoneOne}
                                                error={!!updateErrors.phoneOne}
                                                helperText={updateErrors.phoneOne}
                                                onChange={handleChange}
                                            />
                                        </Box>
                                        <Box width={isMobile ? '100%' : '49%'}>
                                            <CustomInput
                                                label={t('phoneTwo')}
                                                placeholder={t('phoneTwoHere')}
                                                name="phoneTwo"
                                                type='text'
                                                value={formValues.phoneTwo}
                                                error={!!updateErrors.phoneTwo}
                                                helperText={updateErrors.phoneTwo}
                                                onChange={handleChange}
                                            /></Box>
                                    </Box>
                                </Box>


                                <Box sx={{
                                    width: isMobile ? '80%' : '36%'
                                }}>
                                    <UpdateImageUpload
                                        label={t('logo')}
                                        name={t('logo')}
                                        value={'deleteLogo'}
                                        onChange={handleLogoChange}
                                        image={`${fileApiRoute}/${business.logo}`}
                                        setFormValues={setFormValues}
                                    />
                                </Box>

                            </Box>

                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                flexDirection: isMobile ? 'column' : 'row'
                            }}>

                                <Box width={isMobile ? '100%' : '32%'}>
                                    <CustomInput
                                        label={t('addressOneKurdish')}
                                        placeholder={t('addressOneKurdishHere')}
                                        name="addressOneKu"
                                        type='text'
                                        value={formValues.addressOneKu}
                                        error={!!updateErrors.addressOneKu}
                                        helperText={updateErrors.addressOneKu}
                                        onChange={handleChange}
                                    />
                                </Box>

                                <Box width={isMobile ? '100%' : '32%'}>
                                    <CustomInput
                                        label={t('addressOneEnglish')}
                                        placeholder={t('addressOneEnglishHere')}
                                        name="addressOneEn"
                                        type='text'
                                        value={formValues.addressOneEn}
                                        error={!!updateErrors.addressOneEn}
                                        helperText={updateErrors.addressOneEn}
                                        onChange={handleChange}
                                    />
                                </Box>

                                <Box width={isMobile ? '100%' : '32%'}>
                                    <CustomInput
                                        label={t('addressOneArabic')}
                                        placeholder={t('addressOneArabicHere')}
                                        name="addressOneAr"
                                        type='text'
                                        value={formValues.addressOneAr}
                                        error={!!updateErrors.addressOneAr}
                                        helperText={updateErrors.addressOneAr}
                                        onChange={handleChange}
                                    />
                                </Box>
                            </Box>

                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                flexDirection: isMobile ? 'column' : 'row'
                            }}>

                                <Box width={isMobile ? '100%' : '32%'}>
                                    <CustomInput
                                        label={t('addressTwoKurdish')}
                                        placeholder={t('addressTwoKurdishHere')}
                                        name="addressTwoKu"
                                        type='text'
                                        value={formValues.addressTwoKu}
                                        error={!!updateErrors.addressTwoKu}
                                        helperText={updateErrors.addressTwoKu}
                                        onChange={handleChange}
                                    />
                                </Box>

                                <Box width={isMobile ? '100%' : '32%'}>
                                    <CustomInput
                                        label={t('addressTwoEnglish')}
                                        placeholder={t('addressTwoEnglishHere')}
                                        name="addressTwoEn"
                                        type='text'
                                        value={formValues.addressTwoEn}
                                        error={!!updateErrors.addressTwoEn}
                                        helperText={updateErrors.addressTwoEn}
                                        onChange={handleChange}
                                    />
                                </Box>

                                <Box width={isMobile ? '100%' : '32%'}>
                                    <CustomInput
                                        label={t('addressTwoArabic')}
                                        placeholder={t('addressTwoArabicHere')}
                                        name="addressTwoAr"
                                        type='text'
                                        value={formValues.addressTwoAr}
                                        error={!!updateErrors.addressTwoAr}
                                        helperText={updateErrors.addressTwoAr}
                                        onChange={handleChange}
                                    />
                                </Box>
                            </Box>

                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                flexDirection: isMobile ? 'column' : 'row'
                            }}>
                                <Box width={isMobile ? '100%' : '32%'}>
                                    <CustomInputWithSize
                                        label={t('aboutKurdish')}
                                        placeholder={t('aboutKurdishHere')}
                                        name="aboutKu"
                                        type='text'
                                        value={formValues.aboutKu}
                                        error={!!updateErrors.aboutKu}
                                        helperText={updateErrors.aboutKu}
                                        onChange={handleChange}
                                        height={120}
                                    />
                                </Box>

                                <Box width={isMobile ? '100%' : '32%'}>
                                    <CustomInputWithSize
                                        label={t('aboutEnglish')}
                                        placeholder={t('aboutEnglishHere')}
                                        name="aboutEn"
                                        type='text'
                                        value={formValues.aboutEn}
                                        error={!!updateErrors.aboutEn}
                                        helperText={updateErrors.aboutEn}
                                        onChange={handleChange}
                                        height={120}
                                    />
                                </Box>

                                <Box width={isMobile ? '100%' : '32%'}>
                                    <CustomInputWithSize
                                        label={t('aboutArabic')}
                                        placeholder={t('aboutArabicHere')}
                                        name="aboutAr"
                                        type='text'
                                        value={formValues.aboutAr}
                                        error={!!updateErrors.aboutAr}
                                        helperText={updateErrors.aboutAr}
                                        onChange={handleChange}
                                        height={120}
                                    />
                                </Box>
                            </Box>

                            {updateErrors.general && (
                                <Typography align="center" color="error" variant="body2" sx={{ mt: 2 }}>
                                    {updateErrors.general}
                                </Typography>
                            )}

                            <Box sx={{
                                display: 'flex', justifyContent: 'center', alignItems: 'center',
                                mt: updateErrors.general ? 2 : 4
                            }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    sx={{ textTransform: 'none', width: 140, height: 40 }}
                                >
                                    {isLoading ? <CircularProgress color="secondary" size={28} /> :
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                            <p>{t('update')}</p>
                                            <DriveFileRenameOutlineOutlinedIcon fontSize="small" sx={{ mx: 0.4 }} />
                                        </Box>
                                    }
                                </Button>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: isMobile ? 'repeat(1, 1fr)' : 'repeat(2, 1fr)',
                                    gap: 2,
                                    mt: 2,
                                }}
                            >
                                {business.businessImages.map((image, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            width: '100%',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <Avatar
                                            src={image instanceof File ? URL.createObjectURL(image) : `${fileApiRoute}/${image.image || image}`}
                                            variant="rounded"
                                            sx={{
                                                width: '90%',
                                                height: '300px',
                                                objectFit: 'cover',
                                            }}
                                        />
                                        <Box sx={{
                                            color: theme.palette.text.red,
                                            mx: 1,
                                            cursor: 'pointer'
                                        }}>
                                            <CloseOutlinedIcon onClick={() => handleDelete(image.id)} />
                                        </Box>
                                    </Box>
                                ))}
                            </Box>


                        </Box>
                }

                <Box sx={{
                    display: 'flex',
                    my: 2,
                    justifyContent: 'center',
                    width: '100%'
                }}>
                    <input
                        type="file"
                        ref={inputRef}
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                        accept="image/*"
                    />

                    <Button variant="text" sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer'
                    }}
                        onClick={handleButtonClick}
                    >
                        {isImageLoading ? <CircularProgress color="primary" size={28} /> :
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <p
                                    style={{
                                        color: theme.palette.text.primary,
                                        fontSize: 14,
                                    }}
                                >
                                    {t('addImage')}
                                </p>
                                <AddIcon fontSize="small" sx={{ mx: 0.4 }} />
                            </Box>
                        }
                    </Button >
                </Box>

            </Box>
        </Box>
    );
}

export default CompanyInfo;